import type React from 'react';
import { useCallback } from 'react';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItemRadio,
  Button,
} from '@fluentui/react-components';
import { GlobeRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { changeLanguage, getCurrentLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from 'i18n';

const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  lv: 'Latviešu',
  en: 'English',
};

const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  lv: '🇱🇻',
  en: '🇬🇧',
};

interface LanguageSelectorProps {
  /** Optional className for additional styling */
  className?: string;
  /** When true, show only the 2-char language code (e.g. "EN") instead of flag + full name. */
  compact?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className, compact = false }) => {
  const { i18n } = useTranslation();
  const currentLang = getCurrentLanguage();

  const handleLanguageChange = useCallback((lang: SupportedLanguage) => {
    changeLanguage(lang);
  }, []);

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          icon={<GlobeRegular />}
          className={className}
          style={{ color: 'inherit' }}
        >
          {compact ? currentLang.toUpperCase() : `${LANGUAGE_FLAGS[currentLang]} ${LANGUAGE_LABELS[currentLang]}`}
        </Button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList
          checkedValues={{ language: [i18n.language] }}
          onCheckedValueChange={(_, data) => {
            const newLang = data.checkedItems[0] as SupportedLanguage;
            if (newLang) {
              handleLanguageChange(newLang);
            }
          }}
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <MenuItemRadio key={lang} name="language" value={lang}>
              {LANGUAGE_FLAGS[lang]} {LANGUAGE_LABELS[lang]}
            </MenuItemRadio>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};
